import { basketSections } from "../Session/plan-lesson/ai-lesson-planning/recommendationsApi"
import type {
  AiSubject,
  RecommendationApiData,
  RecommendationApiLearner,
} from "../Session/plan-lesson/ai-lesson-planning/aiLessonPlanningTypes"

export interface LearnerAttempt {
  id: string
  subject: AiSubject
  source_id: string
  type_label: string
  title: string
  skill: string
  strand: string
  year: number | null
  score: number | null
  attempted_on: string
}

export interface CurriculumSkill {
  subject: AiSubject
  skill: string
  strand: string
  year: number
}

export interface RecommendedPlacement {
  source_id: string
  subject: AiSubject
  skill: string
  strand: string
  year: number | null
}

export interface LearnerReportData {
  student: {
    pk_student_key: string
    name: string
    current_year: number | null
    country: string | null
    subjects: AiSubject[]
  }
  attempts: LearnerAttempt[]
  curriculum: CurriculumSkill[]
  recommended: RecommendedPlacement[]
}

const SUBJECTS: AiSubject[] = ["math", "english"]

/** Builds the learner report from the CRS v2 recommendations responses (one per subject). */
export function buildLearnerReport(
  studentKey: string,
  studentName: string,
  bySubject: Partial<Record<AiSubject, RecommendationApiData | null>>
): LearnerReportData {
  const loaded = SUBJECTS.filter(s => bySubject[s])
  const learner: RecommendationApiLearner =
    loaded.map(s => bySubject[s]?.learner).find(Boolean) || {}
  const firstYear = loaded
    .map(s => bySubject[s]?.yearUsed)
    .find((y): y is number => typeof y === "number")
  const currentYear =
    typeof learner.profileYear === "number" ? learner.profileYear : firstYear ?? null

  const curriculum: CurriculumSkill[] = []
  const recommended: RecommendedPlacement[] = []
  loaded.forEach(subject => {
    const data = bySubject[subject] as RecommendationApiData
    const strands = new Set<string>()
    basketSections(data).forEach(section =>
      section.items.forEach(item => {
        if (!item?.sourceId) return
        const strand = item.strand || item.domain || ""
        const levels = Array.isArray(item.levels) ? item.levels.filter(n => typeof n === "number") : []
        recommended.push({
          source_id: item.sourceId,
          subject,
          skill: strand,
          strand,
          year: levels.length ? levels[0] : typeof item.year === "number" ? item.year : null,
        })
        if (strand) strands.add(strand)
      })
    )
    if (currentYear !== null) {
      strands.forEach(strand => curriculum.push({ subject, skill: strand, strand, year: currentYear }))
    }
  })

  return {
    student: {
      pk_student_key: studentKey,
      name: studentName,
      current_year: currentYear,
      country: learner.country || null,
      subjects: loaded,
    },
    attempts: [],
    curriculum,
    recommended,
  }
}
